import { FC, ReactElement } from 'react';
import { Avatar, Checkbox, Col, Dropdown, Row } from 'antd';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { DragDropContext, Draggable, DragHandle, Droppable, DropResult } from '@/shared/components/drag-and-drop';
import { DropdownMenuPanel } from '@/shared/components/dropdowns/DropdownPanel';
import LearnMoreFooter from '@/shared/components/layouts/footers/LearnMoreFooter';
import SmallTag from '@/shared/components/tags/SmallTag';
import SmallTooltip from '@/shared/components/tooltips/SmallTooltip';

import {
    EntityDetailTabCustomizationItem,
    EntityDetailTabPreferences,
    orderEntityDetailTabItems,
} from '../EntityDetailTabsCustomizationModal/preferences';

interface EntityDetailTabsReorderDropdownProps {
    children: ReactElement;
    currentKey: string;
    items: EntityDetailTabCustomizationItem[];
    label: string;
    preferences?: EntityDetailTabPreferences;
    resourceType: string;
    setPreference: (path: string, value: unknown) => void;
}

const EntityDetailTabsReorderDropdown: FC<EntityDetailTabsReorderDropdownProps> = ({
    children,
    currentKey,
    items,
    label,
    preferences,
    resourceType,
    setPreference,
}) => {
    const { token } = useAntDesignTheme();
    const orderedItems = orderEntityDetailTabItems(items, preferences);
    const hidden = new Set(preferences?.hidden ?? []);
    const preferencePath = `entityDetail.tabs.${resourceType}`;

    const handleDragEnd = (result: DropResult) => {
        if (!result.destination || result.destination.index === result.source.index) {
            return;
        }

        const nextOrder = orderedItems.map((item) => item.key);
        const [movedKey] = nextOrder.splice(result.source.index, 1);

        nextOrder.splice(result.destination.index, 0, movedKey);
        setPreference(`${preferencePath}.order`, nextOrder);
    };

    const handleVisibilityToggle = (item: EntityDetailTabCustomizationItem) => {
        if (item.locked || item.key === currentKey) {
            return;
        }

        const nextHidden = new Set(hidden);

        if (nextHidden.has(item.key)) {
            nextHidden.delete(item.key);
        } else {
            nextHidden.add(item.key);
        }

        setPreference(`${preferencePath}.hidden`, Array.from(nextHidden));
    };

    return (
        <Dropdown
            getPopupContainer={() => document.body}
            overlayStyle={{ zIndex: token.zIndexPopupBase + 100 }}
            popupRender={() => (
                <DropdownMenuPanel style={{ marginTop: '0.5rem', width: 16 * 24 }}>
                    <div style={{ marginBottom: '0.25rem', padding: '0 0.75rem' }}>
                        <small style={{ color: token.colorTextTertiary }}>Reorder {label} Tabs</small>
                    </div>
                    <DragDropContext onDragEnd={handleDragEnd}>
                        <Droppable droppableId={`entity-detail-tabs-quick:${resourceType}`}>
                            {(provided) => (
                                <div ref={provided.innerRef} {...provided.droppableProps} style={{ width: '100%' }}>
                                    {orderedItems.map((item, index) => {
                                        const currentlyViewing = item.key === currentKey;

                                        return (
                                            <Draggable
                                                draggableId={`entity-detail-tabs-quick:${resourceType}:${item.key}`}
                                                index={index}
                                                key={item.key}
                                            >
                                                {(provided, snapshot) => (
                                                    <Row
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        align='middle'
                                                        gutter={[8, 0]}
                                                        style={{
                                                            backgroundColor: snapshot.isDragging
                                                                ? token.colorBgElevated
                                                                : undefined,
                                                            borderRadius: token.borderRadiusSM,
                                                            margin: snapshot.isDragging ? 0 : '0.375rem 0',
                                                            padding: '0 0.5rem',
                                                            ...provided.draggableProps.style,
                                                        }}
                                                        wrap={false}
                                                    >
                                                        <Col flex='none'>
                                                            <DragHandle
                                                                dragHandleProps={provided.dragHandleProps}
                                                                isDragging={snapshot.isDragging}
                                                                label={`Reorder ${String(item.label)}`}
                                                            />
                                                        </Col>
                                                        <Col flex='none'>
                                                            <SmallTooltip
                                                                placement='left'
                                                                title={
                                                                    item.locked
                                                                        ? 'Required'
                                                                        : currentlyViewing
                                                                          ? 'Currently Viewing'
                                                                          : 'Show In Detail View'
                                                                }
                                                            >
                                                                <Checkbox
                                                                    checked={item.locked || !hidden.has(item.key)}
                                                                    disabled={item.locked || currentlyViewing}
                                                                    onChange={() => handleVisibilityToggle(item)}
                                                                />
                                                            </SmallTooltip>
                                                        </Col>
                                                        <Col flex='none'>
                                                            <Avatar
                                                                icon={item.icon}
                                                                shape='square'
                                                                size={20}
                                                                style={{
                                                                    background: token.colorBgContainer,
                                                                    border: `1px solid ${token.colorBorder}`,
                                                                    color: token.colorTextTertiary,
                                                                }}
                                                            />
                                                        </Col>
                                                        <Col flex='auto' style={{ minWidth: 0 }}>
                                                            <span>{item.label}</span>
                                                        </Col>
                                                        {currentlyViewing ? (
                                                            <Col flex='none'>
                                                                <SmallTag message='Currently Viewing' />
                                                            </Col>
                                                        ) : null}
                                                    </Row>
                                                )}
                                            </Draggable>
                                        );
                                    })}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                    <LearnMoreFooter topic='detail tab reordering' variant='dropdown' />
                </DropdownMenuPanel>
            )}
            placement='bottomRight'
            trigger={['click']}
        >
            {children}
        </Dropdown>
    );
};

export default EntityDetailTabsReorderDropdown;
